'use no memo'

import { useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { ProjectTabValue } from '@/routes/features/projects/types'
import { useChildProjects, useRootProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import { ProjectViewHeader } from '@/routes/features/projects/components/ProjectViewHeader'
import { ViewsListTable } from '@/routes/features/views/ViewsListTable'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/views/',
)({
  component: SpaceProjectViewsIndexPage,
})

function SpaceProjectViewsIndexPage() {
  const { workspaceSlug, spaceKey, projectKey } = Route.useParams()
  const navigate = useNavigate()
  const workspaceId = useLoginStore((s) => s.workspaceId)

  const { projects: rootProjects, isLoading: spacesLoading } = useRootProjects()
  const space = useMemo<ProjectDocument | null>(() => {
    return rootProjects?.find((p) => {
      if (p.isDeleted) return false
      if (p.projectKey.toLowerCase() !== spaceKey.toLowerCase()) return false
      if (workspaceId && p.envId !== workspaceId) return false
      return true
    }) ?? null
  }, [rootProjects, spaceKey, workspaceId])

  const { projects: childProjects } = useChildProjects(space?.id)
  const project = useMemo<ProjectDocument | null>(() => {
    return childProjects?.find((p) => {
      if (p.isDeleted) return false
      if (p.projectKey.toLowerCase() !== projectKey.toLowerCase()) return false
      return true
    }) ?? null
  }, [childProjects, projectKey])

  const handleTabChange = (tab: ProjectTabValue) => {
    if (tab === 'views') return
    navigate({
      to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
      params: { workspaceSlug, spaceKey, projectKey },
      search: tab === 'tasks' ? {} : { projectTab: tab },
    })
  }

  if (spacesLoading) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!space || !project) return null

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex-shrink-0">
        <ProjectViewHeader
          projectId={project.id}
          activeTab="views"
          onTabChange={handleTabChange}
        />
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <ViewsListTable
          scope="project"
          scopeEntityId={project.id}
          scopeKey={projectKey}
          workspaceSlug={workspaceSlug}
          spaceKey={spaceKey}
        />
      </div>
    </div>
  )
}
